import React, { useMemo } from 'react';
import { useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps, ExecutionStatusData, ExecutionLogEntry } from '@flow-studio/flow-observer-core';
import { useDesignerModeStore, useWorkflowStore } from '@flow-studio/store';
import './ExecutionStatusTabContent.css';

function formatMs(ms?: number): string {
  if (ms === undefined) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function formatDate(isoString?: string): string {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return '—';
  }
}

export const ExecutionStatusTabContent: React.FC<FlowObserverTabContentProps> = ({
  tabId,
  loadState,
  error,
  onRetry,
}) => {
  const statusBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates[tabId]?.dataBuffer ?? []
  );

  const logBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates['execution-logs']?.dataBuffer ?? []
  );

  // Per-canvas-node execution status, keyed by node id — a Loop node re-executes the
  // same physical node once per array item, so this map naturally de-duplicates
  // iterations down to one status per distinct workflow node (unlike the backend's
  // cumulative nodesCompleted counter, which counts every execution).
  const nodeExecutionStates = useDesignerModeStore((s) => s.nodeExecutionStates);

  // The real universe of canvas nodes for this workflow — used to bound distinctNodeCounts
  // below. Some node types (e.g. an AI Agent's internal tool-call turns) emit NodeExecuting/
  // NodeExecuted events keyed by a synthetic per-turn id instead of reusing the canvas node's
  // own id, so without this bound every turn adds a brand-new entry to nodeExecutionStates and
  // the distinct count balloons past the workflow's actual node total.
  const canvasNodes = useWorkflowStore((s) => s.nodes);
  const canvasNodeIds = useMemo(() => new Set(canvasNodes.map((n) => n.id)), [canvasNodes]);

  const distinctNodeCounts = useMemo(() => {
    let success = 0;
    let failed = 0;
    let skipped = 0;
    nodeExecutionStates.forEach((state, nodeId) => {
      // Guard on size so an empty/not-yet-loaded canvas doesn't zero out every count.
      if (canvasNodeIds.size > 0 && !canvasNodeIds.has(nodeId)) return;
      const s = (state.status || '').toLowerCase();
      if (s === 'success') success++;
      else if (s === 'failed' || s === 'error' || s === 'timeout') failed++;
      else if (s === 'skipped') skipped++;
    });
    return { success, failed, skipped };
  }, [nodeExecutionStates, canvasNodeIds]);

  const latest = useMemo(() => {
    return statusBuffer.length > 0
      ? (statusBuffer[statusBuffer.length - 1] as ExecutionStatusData)
      : null;
  }, [statusBuffer]);

  const eventStats = useMemo(() => {
    const logs = logBuffer as ExecutionLogEntry[];
    return {
      total: logs.length,
      errors: logs.filter(l => l.level === 'error').length,
      warnings: logs.filter(l => l.level === 'warn').length,
      info: logs.filter(l => l.level === 'info').length,
      debug: logs.filter(l => l.level === 'debug').length,
    };
  }, [logBuffer]);

  if (loadState === 'loading') {
    return <div className="fop-loading">Loading...</div>;
  }

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  if (!latest) {
    return (
      <div className="fop-empty">
        <span>Click Execute to start workflow</span>
      </div>
    );
  }

  // Prefer the real canvas node count over the backend-reported nodesTotal. Some node
  // types (e.g. a multi-step Form node — see the "Form Node 7/7" breadcrumb) are counted
  // server-side as several internal elements even though they render as a single canvas
  // node, which inflated nodesTotal past the workflow's actual node count. That inflated
  // total then fed the "fill remaining as completed on clean finish" rule below, forcing
  // Completed to match the inflated Total regardless of how many real nodes had actually
  // reported success — e.g. a 2-node workflow showing "6 nodes completed".
  const total = canvasNodeIds.size > 0 ? canvasNodeIds.size : (latest.nodesTotal ?? 0);
  // Backend only reliably reports nodesTotal on the final completion event — while
  // running it's 0, which used to clamp `completed` down to 0 via Math.min(total, ...)
  // even though distinctNodeCounts (derived from live canvas node statuses) already
  // knows real nodes have succeeded. Only clamp once we actually know the total; while
  // it's unknown, report the live distinct counts directly instead of zeroing them out.
  const knownTotal = total > 0;
  // Use distinct per-node status (bounded by `total` once known) rather than the backend's
  // raw nodesCompleted/nodesFailed counters — those count every execution of a node, so a
  // Loop node that re-runs the same 4 canvas nodes across many array items produces a
  // "completed" count far larger than the node total, which reads as nonsensical.
  let completed = knownTotal ? Math.min(total, distinctNodeCounts.success) : distinctNodeCounts.success;
  let failed = knownTotal ? Math.min(total - completed, distinctNodeCounts.failed) : distinctNodeCounts.failed;
  let skipped = knownTotal ? Math.min(total - completed - failed, distinctNodeCounts.skipped) : distinctNodeCounts.skipped;
  let remaining = knownTotal ? Math.max(0, total - completed - failed - skipped) : 0;

  // Some nodes (e.g. the trigger) may never receive a per-node status update, which
  // would otherwise leave them stuck in "remaining" even though the run finished —
  // if the workflow as a whole completed successfully, count any such gap as completed.
  if (latest.runState === 'completed' && remaining > 0) {
    completed += remaining;
    remaining = 0;
  }

  const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const avgTimePerNode = completed > 0 ? Math.round((latest.elapsedMs ?? 0) / completed) : 0;

  // Process/Thread completion has no dedicated per-entity signal from the backend, unlike
  // Node Completed (nodeExecutionStates via SignalR node-status events). Previously this scanned
  // the raw log buffer for entries whose rawDetail.item.entityType matched 'Process'/'ProcessThread'
  // and whose message text contained the substring "completed" — if the backend didn't emit a log
  // entry shaped exactly that way, the count stuck at 0 even though the run had clearly finished.
  // A single execution has exactly one Process and one root ProcessThread, so runState === 'completed'
  // (the same reliable field Node Completed's fallback already relies on) is a correct proxy.
  const processCompleted = latest.runState === 'completed' ? 1 : 0;
  const threadCompleted = latest.runState === 'completed' ? 1 : 0;

  return (
    <div className="fop-exec-status">
      {/* Status Header */}
      <div className="fop-status-header">
        <div className={`fop-state-badge fop-state-badge--${latest.runState}`}>
          {latest.runState.toUpperCase()}
        </div>
        {latest.currentNodeLabel && (
          <div className="fop-current-node">
            <span className="fop-label">Current Node:</span>
            <span className="fop-value">{latest.currentNodeLabel}</span>
          </div>
        )}
      </div>

      {/* Progress Section */}
      <div className="fop-section">
        <h3 className="fop-section-title">Execution Progress</h3>
        <div className="fop-progress-container">
          <div className="fop-progress-bar" aria-label={`${pct}% complete`}>
            <div className="fop-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="fop-progress-text">{pct}%</div>
        </div>
      </div>

      {/* Node Statistics */}
      {total > 0 && (
        <div className="fop-section">
          <h3 className="fop-section-title">Node Statistics</h3>
          <div className="fop-stats-grid">
            <div className="fop-stat-card">
              <div className="fop-stat-label">Total</div>
              <div className="fop-stat-value fop-stat-value--primary">{total}</div>
            </div>
            <div className="fop-stat-card">
              <div className="fop-stat-label">Completed</div>
              <div className="fop-stat-value fop-stat-value--success">{completed}</div>
            </div>
            <div className="fop-stat-card">
              <div className="fop-stat-label">Failed</div>
              <div className={`fop-stat-value ${failed > 0 ? 'fop-stat-value--error' : ''}`}>
                {failed}
              </div>
            </div>
            <div className="fop-stat-card">
              <div className="fop-stat-label">Skipped</div>
              <div className="fop-stat-value">{skipped}</div>
            </div>
            <div className="fop-stat-card">
              <div className="fop-stat-label">Remaining</div>
              <div className="fop-stat-value">{remaining}</div>
            </div>
          </div>
        </div>
      )}

      {/* Entity Completions */}
      <div className="fop-section">
        <h3 className="fop-section-title">Entity Completions</h3>
        <div className="fop-stats-grid">
          <div className="fop-stat-card">
            <div className="fop-stat-label">Process Completed</div>
            <div className="fop-stat-value fop-stat-value--success">{processCompleted}</div>
          </div>
          <div className="fop-stat-card">
            <div className="fop-stat-label">Thread Completed</div>
            <div className="fop-stat-value fop-stat-value--primary">{threadCompleted}</div>
          </div>
          <div className="fop-stat-card">
            <div className="fop-stat-label">Node Completed</div>
            <div className="fop-stat-value">{completed}</div>
          </div>
        </div>
      </div>

      {/* Event Statistics */}
      <div className="fop-section">
        <h3 className="fop-section-title">Event Statistics</h3>
        <div className="fop-stats-grid">
          <div className="fop-stat-card">
            <div className="fop-stat-label">Total Events</div>
            <div className="fop-stat-value fop-stat-value--primary">{eventStats.total}</div>
          </div>
          <div className="fop-stat-card">
            <div className="fop-stat-label">Info</div>
            <div className="fop-stat-value">{eventStats.info}</div>
          </div>
          <div className="fop-stat-card">
            <div className="fop-stat-label">Warnings</div>
            <div className="fop-stat-value fop-stat-value--warn">{eventStats.warnings}</div>
          </div>
          <div className="fop-stat-card">
            <div className="fop-stat-label">Errors</div>
            <div className={`fop-stat-value ${eventStats.errors > 0 ? 'fop-stat-value--error' : ''}`}>
              {eventStats.errors}
            </div>
          </div>
          <div className="fop-stat-card">
            <div className="fop-stat-label">Debug</div>
            <div className="fop-stat-value">{eventStats.debug}</div>
          </div>
        </div>
      </div>

      {/* Timing Information */}
      <div className="fop-section">
        <h3 className="fop-section-title">Timing Information</h3>
        <div className="fop-timing-grid">
          <div className="fop-timing-row">
            <span className="fop-label">Duration</span>
            <span className="fop-value">{formatMs(latest.elapsedMs)}</span>
          </div>
          {completed > 0 && (
            <div className="fop-timing-row">
              <span className="fop-label">Avg Time per Node</span>
              <span className="fop-value">{formatMs(avgTimePerNode)}</span>
            </div>
          )}
          {latest.startedAt && (
            <div className="fop-timing-row">
              <span className="fop-label">Started At</span>
              <span className="fop-value">{formatDate(latest.startedAt)}</span>
            </div>
          )}
          {latest.completedAt && (
            <div className="fop-timing-row">
              <span className="fop-label">Completed At</span>
              <span className="fop-value">{formatDate(latest.completedAt)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Error Section */}
      {latest.errorMessage && (
        <div className="fop-error-section" role="alert">
          <h3 className="fop-section-title fop-section-title--error">Error</h3>
          <div className="fop-error-message">{latest.errorMessage}</div>
        </div>
      )}
    </div>
  );
};
